import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import StandardsSection from '../components/StandardsSection';
import {
  FaArrowRight,
  FaCertificate,
  FaCheckCircle,
  FaClipboardCheck,
  FaClipboardList,
  FaCogs,
  FaExclamationCircle,
  FaFileAlt,
  FaGraduationCap,
  FaHardHat,
  FaHistory,
  FaIdBadge,
  FaInfoCircle,
  FaQrcode,
  FaSearch,
  FaShieldAlt,
  FaTools,
  FaUsers,
  FaUserCheck
} from 'react-icons/fa';

import '../styles/training-certification.css';

import heroImage from '../pics/Enginner1.png';

/*
 * Training programmes and service content mirror the existing
 * Services page so both pages describe the same offering.
 */
const trainingPrograms = [
  {
    code: 'TRN-01',
    title: 'Mobile Crane Operator Training',
    subtitle: 'Operator competence • Practical and theoretical',
    icon: <FaGraduationCap />,
    accent: false,
    desc:
      'Practical and theoretical training focused on safe crane operation, lifting awareness, equipment checks, and responsible operating practices.',
    modules: [
      'Operator safety fundamentals',
      'Pre-use equipment checks',
      'Load and lifting awareness',
      'Safe operating procedures'
    ],
    outcome: 'Operator Competence'
  },
  {
    code: 'TRN-02',
    title: 'Rigging & Lifting Training',
    subtitle: 'Rigging practice • Load handling',
    icon: <FaHardHat />,
    accent: true,
    desc:
      'Hands-on instruction for personnel involved in rigging, lifting, load handling, and field support activities.',
    modules: [
      'Rigging equipment awareness',
      'Load handling practices',
      'Slinging techniques',
      'Communication during lifting'
    ],
    outcome: 'Safe Lifting Practice'
  },
  {
    code: 'TRN-03',
    title: 'Crane Inspector Certification',
    subtitle: 'Inspection programme • Certification preparation',
    icon: <FaClipboardCheck />,
    accent: false,
    desc:
      'Structured training and certification preparation for personnel developing competence in crane and lifting equipment inspection.',
    modules: [
      'Inspection principles',
      'Defect identification',
      'Inspection documentation',
      'Certification procedures'
    ],
    outcome: 'Inspection Competence'
  },
  {
    code: 'TRN-04',
    title: 'Equipment Inspection & Certification',
    subtitle: 'Equipment programme • Documentation',
    icon: <FaTools />,
    accent: true,
    desc:
      'Inspection-focused services designed to help organisations maintain confidence in lifting equipment condition and documentation.',
    modules: [
      'Equipment assessment',
      'Inspection reporting',
      'Certification records',
      'Operational recommendations'
    ],
    outcome: 'Inspection & Documentation'
  }
];

/*
 * Hero content: what the certification service covers, and the
 * assurances behind an issued certificate. Kept consistent with the
 * training programmes and the quality points further down the page.
 */
const heroScope = [
  'Mobile Cranes',
  'Rigging & Lifting',
  'Lifting Equipment',
  'Pressure Testing',
  'Operator Competence'
];

const heroPoints = [
  {
    icon: <FaQrcode />,
    title: 'Verifiable by ID or QR',
    desc: 'Every certificate carries an ID and QR code that resolve to this service.'
  },
  {
    icon: <FaIdBadge />,
    title: 'Named, credentialed inspectors',
    desc: 'Equipment certificates are signed off by an identified inspector.'
  },
  {
    icon: <FaClipboardCheck />,
    title: 'Stated reference standards',
    desc: 'Test certificates record the procedure and standards applied.'
  },
  {
    icon: <FaHistory />,
    title: 'Dated validity',
    desc: 'Tested, next-test and expiry dates are recorded so currency is clear.'
  }
];

const processSteps = [
  {
    num: '01',
    title: 'Request & Assessment',
    icon: <FaClipboardList />,
    desc:
      'We review the equipment, personnel, training requirements, or project scope to establish the appropriate service approach.',
    output: 'Scope & Requirement Review'
  },
  {
    num: '02',
    title: 'Technical Preparation',
    icon: <FaCogs />,
    desc:
      'Our team prepares the appropriate personnel, equipment, training materials, and inspection requirements for the assignment.',
    output: 'Prepared Service Delivery'
  },
  {
    num: '03',
    title: 'Inspection or Training',
    icon: <FaHardHat />,
    desc:
      'Training, inspection, testing, or certification activities are carried out with emphasis on safety, accuracy, and practical competence.',
    output: 'Field-Level Execution'
  },
  {
    num: '04',
    title: 'Certification & Verification',
    icon: <FaCertificate />,
    desc:
      'Results and certification documentation are compiled, issued with a certificate ID, and made verifiable online through this platform.',
    output: 'Verifiable Certificate'
  }
];

/*
 * Field reference for a DOFCAN certificate record. These are the
 * fields the certificate API actually returns - no specimen values
 * are shown here so nothing can be mistaken for a real certificate.
 */
const certificateFields = [
  {
    label: 'Certificate ID',
    value: 'certificateId',
    note: 'The reference printed on the document. IDs may contain slashes, for example DOF/CP/… or DOL/NAT/… formats.'
  },
  {
    label: 'Recipient',
    value: 'recipientName',
    note: 'The individual the training or professional certificate was issued to.'
  },
  {
    label: 'Title',
    value: 'certificateTitle',
    note: 'The training programme or certification the document covers.'
  },
  {
    label: 'Certificate type',
    value: 'certificateType',
    note: 'Training, Training Certification, Professional Certification, Equipment Certification and related types.'
  },
  {
    label: 'Issued / expiry',
    value: 'issuedDate · expiryDate',
    note: 'Issue date, and where an expiry applies, the date the certificate ceases to be current.'
  },
  {
    label: 'Issuer',
    value: 'issuerName · issuerCompanyId',
    note: 'The issuing organisation recorded against the certificate.'
  }
];

/*
 * Inspection checklist structure used on DOFCAN equipment
 * certificates (as recorded on issued equipment certificates).
 */
const equipmentChecklist = [
  { description: 'Visual inspection', observation: 'Condition assessed on site', remarks: 'Recorded as satisfactory / unsatisfactory' },
  { description: 'Inlet valve', observation: 'Working condition checked', remarks: 'Recorded against the item' },
  { description: 'Outlet valve', observation: 'Working condition checked', remarks: 'Recorded against the item' },
  { description: 'Other attachments', observation: 'Condition and function checked', remarks: 'Recorded against the item' }
];

const verificationReasons = [
  {
    num: '01',
    icon: <FaShieldAlt />,
    title: 'Confirm authenticity',
    desc:
      'Check that a certificate presented to you corresponds to a record issued by DOFCAN Oilserve, rather than relying on the paper copy alone.'
  },
  {
    num: '02',
    icon: <FaSearch />,
    title: 'Look up by ID or email',
    desc:
      'Verification accepts a certificate ID directly, and can also list the certificates held against a recipient email address.'
  },
  {
    num: '03',
    icon: <FaQrcode />,
    title: 'Scan the QR code',
    desc:
      'Certificates carry a QR code that resolves to this verification service. You can scan it with a camera or upload a QR image.'
  },
  {
    num: '04',
    icon: <FaHistory />,
    title: 'Check currency',
    desc:
      'Where an expiry or next-test date applies, verification shows whether the certificate is still current or has lapsed.'
  }
];

const qualityPoints = [
  {
    icon: <FaFileAlt />,
    title: 'Documented procedures',
    desc:
      'Equipment certificates record the procedure reference used for the assignment, so the basis of the result is traceable on the document.',
    code: 'DOL-TTS-002'
  },
  {
    icon: <FaClipboardCheck />,
    title: 'Stated reference standards',
    desc:
      'Pressure-testing certificates state the reference standards applied to the assignment alongside the recorded test conditions.',
    code: 'ASME B31.3 · ISO 5208:2015'
  },
  {
    icon: <FaIdBadge />,
    title: 'Named inspector & credential',
    desc:
      'Each equipment certificate carries the name and credential reference of the inspector who carried out and signed off the work.'
  },
  {
    icon: <FaHistory />,
    title: 'Test and retest dating',
    desc:
      'Certificates record the tested date, the next test date, and the validity period so currency can be checked at a glance.'
  },
  {
    icon: <FaQrcode />,
    title: 'QR-linked verification',
    desc:
      'Issued certificates carry a QR code that resolves to this verification service, so the record can be checked without contacting us first.'
  },
  {
    icon: <FaUserCheck />,
    title: 'Recipient records',
    desc:
      'Training and professional certificates are stored against the recipient, allowing the certificates held by a person to be listed.'
  }
];

export default function TrainingCertificationPage() {
  const navigate = useNavigate();

  const [certificateId, setCertificateId] = useState('');
  const [inputError, setInputError] = useState(null);

  /*
   * Verification itself is handled by the existing /verify page and
   * the existing certificates API - this panel only collects the ID
   * and hands it over, so there is a single verification implementation.
   */
  const handleVerify = (event) => {
    event.preventDefault();

    const trimmedId = certificateId.trim();

    if (!trimmedId) {
      setInputError('Enter a certificate ID to verify.');
      return;
    }

    setInputError(null);

    navigate(`/verify?certificateId=${encodeURIComponent(trimmedId)}`);
  };

  const handleInputChange = (event) => {
    setCertificateId(event.target.value);

    if (inputError) {
      setInputError(null);
    }
  };

  return (
    <div className="tc-page">
      <SEO
        title="Training & Certification"
        description="Discover Dofcan Oilserve's training and certification programs, and learn how to verify the authenticity of a Dofcan training certificate online."
        path="/training-certification"
      />

      {/* =====================================================
          HERO + VERIFICATION COMMAND PANEL
      ====================================================== */}
      <section className="tc-hero">
        <div className="tc-hero-grid"></div>
        <div className="tc-hero-glow"></div>

        <div className="tc-container">

          <div className="tc-hero-badges">
            <span className="tc-badge">
              <FaShieldAlt />
              Training • Equipment Certification • Verification
            </span>

            <span className="tc-badge tc-badge-alt">
              <FaCheckCircle />
              Dofcan Oilserve Limited
            </span>

            <span className="tc-specref">
              REF: TRAINING &amp; CERTIFICATION SERVICES
            </span>
          </div>

          <div className="tc-hero-main">

            <div className="tc-hero-copy">
              <h1>
                Professional Training &amp;
                <span> Equipment Certification</span>
              </h1>

              <p>
                Dofcan Oilserve Limited provides professional training,
                equipment inspection and certification services for lifting,
                oil and gas, and industrial operations — and issues
                certificates that can be verified online.
              </p>

              <div className="tc-hero-actions">
                <button
                  type="button"
                  className="tc-btn-primary"
                  onClick={() => navigate('/verify')}
                >
                  Verify a Certificate
                  <FaArrowRight />
                </button>

                <Link className="tc-btn-ghost" to="/services">
                  Explore Our Services
                </Link>
              </div>

              <div className="tc-hero-scope" aria-label="Certification scope">
                <span className="tc-hero-scope-label">Scope</span>
                {heroScope.map((item) => (
                  <span className="tc-hero-scope-tag" key={item}>
                    {item}
                  </span>
                ))}
              </div>

              <div className="tc-hero-points">
                {heroPoints.map((point) => (
                  <div className="tc-hero-point" key={point.title}>
                    <div className="tc-hero-point-icon">{point.icon}</div>
                    <div>
                      <strong>{point.title}</strong>
                      <small>{point.desc}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="tc-hero-visual">
              <img
                src={heroImage}
                alt="Dofcan Oilserve inspector recording an equipment inspection on site"
              />

              <div className="tc-hero-visual-caption">
                <FaHardHat />
                <div>
                  <span>DOFCAN SERVICE STANDARD</span>
                  <strong>Safety • Competence • Documentation</strong>
                </div>
              </div>
            </div>
          </div>

          {/* ============ VERIFICATION PANEL ============ */}
          <div className="tc-verify" id="verify-certificate">

            <div className="tc-verify-top">
              <span className="tc-verify-top-label">
                <FaCertificate />
                Certificate Verification
              </span>

              <span className="tc-verify-status">
                <span className="tc-verify-dot"></span>
                Verification service available
              </span>
            </div>

            <div className="tc-verify-body">

              <div>
                <h2>Check a DOFCAN certificate</h2>

                <p>
                  Enter the certificate ID exactly as printed on the document.
                  Your certificate will be checked against our records on the
                  verification page.
                </p>

                <form className="tc-verify-form" onSubmit={handleVerify}>
                  <div className="tc-verify-input-wrap">
                    <FaSearch />
                    <input
                      type="text"
                      value={certificateId}
                      onChange={handleInputChange}
                      placeholder="e.g. DOF/CP/1226-01"
                      aria-label="Certificate ID"
                      aria-invalid={inputError ? 'true' : 'false'}
                    />
                  </div>

                  <button type="submit" className="tc-btn-primary">
                    Verify Certificate
                    <FaArrowRight />
                  </button>

                  {inputError && (
                    <p className="tc-verify-error" role="alert">
                      <FaExclamationCircle />
                      {inputError}
                    </p>
                  )}
                </form>

                <p className="tc-verify-hint">
                  <FaInfoCircle />
                  <span>
                    Certificate IDs containing slashes or spaces are supported —
                    enter them as printed, for example{' '}
                    <code>DOF/CP/1226-01</code>.
                  </span>
                </p>
              </div>

              <div className="tc-verify-methods">
                <span>Other ways to verify</span>

                <div className="tc-verify-method">
                  <FaQrcode />
                  <div>
                    <strong>Scan the certificate QR code</strong>
                    <small>
                      Use your camera, or upload a QR image on the verification
                      page.
                    </small>
                  </div>
                </div>

                <div className="tc-verify-method">
                  <FaSearch />
                  <div>
                    <strong>Search by recipient email</strong>
                    <small>
                      List the certificates held against an email address.
                    </small>
                  </div>
                </div>

                <div className="tc-verify-method">
                  <FaFileAlt />
                  <div>
                    <strong>Open the full verification page</strong>
                    <small>
                      <Link to="/verify">Go to certificate verification</Link>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* =====================================================
          METRICS
      ====================================================== */}
      <section className="tc-section" data-reveal>
        <div className="tc-container">
          <div className="tc-metrics">

            <div className="tc-metric">
              <div className="tc-metric-top">
                <span>Experience</span>
                <FaShieldAlt />
              </div>
              <strong>15+</strong>
              <p>Years of industry experience</p>
            </div>

            <div className="tc-metric">
              <div className="tc-metric-top">
                <span>Professionals</span>
                <FaUsers />
              </div>
              <strong>3k+</strong>
              <p>Professionals trained</p>
            </div>

            <div className="tc-metric">
              <div className="tc-metric-top">
                <span>Companies</span>
                <FaHardHat />
              </div>
              <strong>500+</strong>
              <p>Companies served</p>
            </div>

            <div className="tc-metric">
              <div className="tc-metric-top">
                <span>Service areas</span>
                <FaTools />
              </div>
              <strong>4</strong>
              <p>Core service categories</p>
            </div>
          </div>
        </div>
      </section>


      {/* =====================================================
          TRAINING PROGRAMMES
      ====================================================== */}
      <section className="tc-section tc-section-alt" id="training" data-reveal>
        <div className="tc-container">

          <div className="tc-section-head">
            <div>
              <span className="tc-eyebrow">Workforce Development</span>
              <h2 className="tc-h2">Training Programmes</h2>
              <p className="tc-lede">
                Practical learning programmes for operators, lifting personnel,
                inspectors, supervisors, and professionals working within
                industrial operations.
              </p>
            </div>

            <div className="tc-section-note">
              <span></span>
              Competence • Assessment • Certification
            </div>
          </div>

          <div className="tc-cards">
            {trainingPrograms.map((program) => (
              <article className="tc-card" key={program.code}>
                <div
                  className={`tc-card-accent ${program.accent ? 'is-red' : ''}`}
                ></div>

                <div className="tc-card-body">
                  <div className="tc-card-head">
                    <div>
                      <div className="tc-chip-row">
                        <span className="tc-code-chip">
                          CODE: {program.code}
                        </span>

                        <span className="tc-status-chip">
                          <FaCheckCircle />
                          Programme available
                        </span>
                      </div>

                      <h3>{program.title}</h3>

                      <span className="tc-card-subtitle">
                        {program.subtitle}
                      </span>
                    </div>

                    <div className="tc-icon-tile">{program.icon}</div>
                  </div>

                  <p>{program.desc}</p>

                  <div className="tc-spec-panel">
                    <span>Programme areas</span>

                    <div className="tc-spec-list">
                      {program.modules.map((module) => (
                        <div key={module}>
                          <FaCheckCircle />
                          {module}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="tc-card-foot">
                    <div>
                      <span>Primary outcome</span>
                      <strong>{program.outcome}</strong>
                    </div>

                    <Link className="tc-btn-light" to="/services">
                      Enquire
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          EQUIPMENT CERTIFICATION
      ====================================================== */}
      <section className="tc-section" id="equipment-certification" data-reveal>
        <div className="tc-container">

          <div className="tc-section-head">
            <div>
              <span className="tc-eyebrow">Equipment Services</span>
              <h2 className="tc-h2">Equipment Inspection &amp; Certification</h2>
              <p className="tc-lede">
                Equipment is identified, inspected and — where applicable —
                tested, then issued with certification documentation that can be
                verified online.
              </p>
            </div>
          </div>

          <div className="tc-record">
            <div className="tc-record-head">
              <strong>EQUIPMENT CERTIFICATE — RECORDED CONTENT</strong>
              <span>INSPECTION CHECKLIST STRUCTURE</span>
            </div>

            <div className="tc-record-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Observation</th>
                    <th>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {equipmentChecklist.map((row) => (
                    <tr key={row.description}>
                      <td>{row.description}</td>
                      <td>{row.observation}</td>
                      <td>{row.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="tc-record-note">
              <FaInfoCircle />
              <span>
                Alongside the checklist, an equipment certificate records the
                item description and equipment ID, the certificate number, the
                client and test location, maximum working pressure where
                applicable, the test conditions, the tested and next test dates,
                the validity period, the procedure reference and reference
                standards, and the inspector&apos;s name and credential.
              </span>
            </p>
          </div>

          <div className="tc-steps" style={{ marginTop: '32px' }}>
            {processSteps.map((step) => (
              <article className="tc-step" key={step.num}>
                <div className="tc-step-top">
                  <span className="tc-step-num">STEP {step.num}</span>
                  {step.icon}
                </div>

                <h3>{step.title}</h3>
                <p>{step.desc}</p>

                <div className="tc-step-out">
                  <span>Output</span>
                  <strong>{step.output}</strong>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          CERTIFICATE ANATOMY
      ====================================================== */}
      <section className="tc-section tc-section-alt" id="certificate" data-reveal>
        <div className="tc-container">
          <div className="tc-preview">

            <div className="tc-preview-copy">
              <span className="tc-eyebrow">Certificate Structure</span>
              <h2>What a DOFCAN certificate contains</h2>

              <p>
                Each certificate is issued against a stored record. The fields
                below are the ones held for a certificate and shown when it is
                verified — so you can check a document in front of you against
                what the record should contain.
              </p>

              <div className="tc-preview-points">
                <div className="tc-preview-point">
                  <FaCertificate />
                  <div>
                    <strong>Certificate image</strong>
                    <small>
                      Where a certificate image was uploaded at issue, it is
                      displayed with the verification result.
                    </small>
                  </div>
                </div>

                <div className="tc-preview-point">
                  <FaQrcode />
                  <div>
                    <strong>QR code</strong>
                    <small>
                      The QR code on a certificate resolves to this
                      verification service with the certificate ID included.
                    </small>
                  </div>
                </div>

                <div className="tc-preview-point">
                  <FaCheckCircle />
                  <div>
                    <strong>Status and dates</strong>
                    <small>
                      Verification reports whether a certificate is valid, and
                      flags it where an expiry date has passed.
                    </small>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="tc-btn-primary"
                onClick={() => navigate('/verify')}
              >
                Verify a Certificate
                <FaArrowRight />
              </button>
            </div>

            <div className="tc-doc">
              <div className="tc-doc-bar"></div>

              <div className="tc-doc-head">
                <div className="tc-doc-head-left">
                  <FaCertificate />
                  <div>
                    <strong>DOFCAN OILSERVE LIMITED</strong>
                    <span>Certificate record fields</span>
                  </div>
                </div>

                <span className="tc-doc-type">FIELD REFERENCE</span>
              </div>

              <div className="tc-doc-fields">
                {certificateFields.map((field) => (
                  <div className="tc-doc-field" key={field.label}>
                    <span>{field.label}</span>

                    <div className="tc-doc-field-value">
                      <strong>{field.value}</strong>
                      <small>{field.note}</small>
                    </div>
                  </div>
                ))}
              </div>

              <div className="tc-doc-foot">
                <div className="tc-doc-qr">
                  <FaQrcode />
                </div>

                <div className="tc-doc-foot-copy">
                  <strong>Scan or enter the ID to verify</strong>
                  <small>
                    This panel is a field reference, not a certificate. Use the
                    verification page to check an actual certificate record.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* =====================================================
          WHY VERIFICATION MATTERS
      ====================================================== */}
      <section className="tc-section-dark" data-reveal>
        <div className="tc-hero-grid"></div>

        <div className="tc-container">
          <div style={{ marginBottom: '36px' }}>
            <span className="tc-eyebrow">Verification</span>
            <h2 className="tc-h2">Why verification matters</h2>
            <p className="tc-lede">
              Verification gives employers, clients and site teams a way to
              confirm a certificate against the issued record.
            </p>
          </div>

          <div className="tc-dark-grid">
            {verificationReasons.map((reason) => (
              <article className="tc-dark-card" key={reason.num}>
                <div className="tc-dark-card-top">
                  <span className="tc-dark-num">{reason.num}</span>
                  {reason.icon}
                </div>

                <h3>{reason.title}</h3>
                <p>{reason.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          QUALITY & CERTIFICATION APPROACH
      ====================================================== */}
      <section className="tc-section" id="quality" data-reveal>
        <div className="tc-container">

          <div className="tc-section-head">
            <div>
              <span className="tc-eyebrow">Quality</span>
              <h2 className="tc-h2">Quality &amp; Certification Approach</h2>
              <p className="tc-lede">
                How DOFCAN certification work is documented and made
                checkable — based on what is recorded on issued certificates.
              </p>
            </div>
          </div>

          <div className="tc-quality" data-reveal="stagger">
            {qualityPoints.map((point) => (
              <article className="tc-quality-card" key={point.title}>
                {point.icon}
                <h3>{point.title}</h3>
                <p>{point.desc}</p>
                {point.code && <code>{point.code}</code>}
              </article>
            ))}
          </div>

          <StandardsSection
            variant="strip"
            eyebrow="Management Standards"
            descriptions={[
              'Consistent, documented delivery of training, inspection, and certification work.',
              'Environmental responsibility considered in how assignments are planned and carried out.',
              'Occupational health and safety at the centre of training, lifting, and equipment work.',
            ]}
            note="Training, equipment certification, inspection, and pressure-testing work is carried out in line with our commitment to quality, environmental responsibility, and occupational health and safety."
            className="tc-standards-strip"
          />
        </div>
      </section>


      {/* =====================================================
          FINAL CTA
      ====================================================== */}
      <section className="tc-cta-section" data-reveal>
        <div className="tc-container">
          <div className="tc-cta">
            <div className="tc-hero-grid"></div>

            <div className="tc-cta-copy">
              <span>Dofcan Oilserve Limited</span>

              <h2>Need Training or Equipment Certification?</h2>

              <p>
                Tell us what you need and our team can review your requirements
                and provide the appropriate next steps — or verify a certificate
                you already hold.
              </p>
            </div>

            <div className="tc-cta-actions">
              <Link className="tc-btn-primary" to="/services">
                Request a Service
                <FaArrowRight />
              </Link>

              <Link className="tc-btn-light" to="/verify">
                Verify a Certificate
                <FaCertificate />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
